<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="680px"
    content-class="fixed-height-md"
  >
    <modal-card
      title="Import Contacts"
      fixed
      :loading="loading"
      :full="isStep(1)"
      @close="closeDialog"
    >
      <image-dropzone
        ref="imageDropzone"
        class="fill-height import-contacts-content pa-4"
        :accepted-formats="acceptedFormats"
        :disabled="!isStep(1)"
        @fileDropped="uploadFile"
      >
        <div
          class="import-placeholder"
          v-if="isStep(1)"
          @click="openFilePicker"
        >
          <div class="import-placeholder__icon">
            <v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
          </div>
          <h3 class="import-placeholder__title">Upload CSV</h3>
          <ol class="styled-list mb-4 mt-2">
            <li>Upload a CSV File</li>
            <li>Tell us about your data</li>
            <li>Start the Import</li>
          </ol>
        </div>

        <ContactsImportMatch
          v-if="isStep(2)"
          ref="importMatch"
          class="pb-4"
          :importedFields="importedFields"
          :keyFields="keyFields"
          @assign="assignField"
        />

        <ContactsImportStatus
          v-if="isStep(3)"
          :import-id="importedContacts.id"
          hide-tag-group
          @status-update="handleStatusUpdate"
        />
      </image-dropzone>
      <template v-slot:actions>
        <template v-if="isStep(2)">
          <v-btn color="secondary" class="mr-4" @click="changeStep()"
            >Prev</v-btn
          >
          <v-btn color="primary" class="mr-2" @click="changeStep(true)"
            >Next</v-btn
          >
        </template>

        <v-btn v-if="isStep(3)" color="appMain" @click="$emit('finish')">
          Finish
        </v-btn>
      </template>
    </modal-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import FilestackLib from '@/lib/filestackLib';

import Vue from 'vue';
import ModalCard from '@/components/basic/modal-card/ModalCard.vue';
import ImageDropzone from '@/components/basic/image-dropzone/ImageDropzone.vue';

import ContactsImportMatch from '@/views/contacts/import/steps/ContactsImportMatch.vue';
import ContactsImportStatus from '@/views/contacts/import/steps/ContactsImportStatus.vue';

export default {
  name: 'ImportContactsDialog',
  components: {
    ImageDropzone,
    ModalCard,
    ContactsImportMatch,
    ContactsImportStatus,
  },
  props: {
    value: Boolean,
  },
  data() {
    return {
      loading: false,
      acceptedFormats: '.csv',
      importedContacts: {},
      fields: {},
      importStep: 1,
    };
  },
  computed: {
    ...mapGetters('fields', ['all', 'getSingleField', 'keyFields']),
    dialogOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    importedRecords() {
      return this.importedContacts?.records || [];
    },

    importedFields() {
      return this.importedRecords[0] || {};
    },

    defaultBucketFields() {
      const defaultBucket = this.all.find((i) => i.type === 'default');
      return (defaultBucket ? Object.keys(defaultBucket.fields) : []).map(
        (i) => {
          return {
            name: this.prepareString(i),
            label: this.prepareString(defaultBucket.fields[i].label),
            field: i,
          };
        }
      );
    },
  },
  watch: {
    importedContacts() {
      this.setFields();
    },
    all() {
      this.setFields();
    },
  },
  methods: {
    ...mapActions('fields', ['load']),
    ...mapActions(['finishImportContacts', 'importContacts']),
    openFilePicker() {
      this.$refs.imageDropzone?.openPicker();
    },

    isStep(step) {
      return this.importStep === step;
    },

    closeDialog() {
      this.dialogOpen = false;
    },

    async changeStep(increase = false) {
      if (this.importStep === 2 && !increase) {
        //return this.showCancelConfirmation();
      }

      if (this.importStep === 2 && increase) {
        if (!this.$refs.importMatch.isValid()) return;
        await this.finishImport();
      }

      this.importStep += increase ? 1 : -1;
    },

    async uploadFile(file) {
      try {
        this.loading = true;

        let fs = new FilestackLib();
        const res = await fs.upload(file);
        await this.extractContacts(res);
      } finally {
        this.loading = false;
      }
    },

    async extractContacts({ url }) {
      if (!url) return;
      this.importedContacts = await this.importContacts(url);
      if (this.importedContacts?.records.length) {
        this.changeStep(true);
      }
      return;
    },

    async finishImport() {
      this.loading = true;
      try {
        await this.finishImportContacts({
          id: this.importedContacts.id,
          fields: this.prepareFields(),
          tags: [],
        });

        this.$emit('checkAchievement');
      } finally {
        this.loading = false;
      }
    },

    prepareFields() {
      return Object.keys(this.fields).reduce((acc, key) => {
        const field = this.fields[key];
        if (!field.skip && key !== 'empty') {
          acc[key] = field;
        }
        return acc;
      }, {});
    },

    checkFields() {
      if (this.all.length) return;
      this.load();
    },

    assignField(keyField) {
      let data = {
        bucket_id: keyField.bucketId,
        name: keyField.name,
        type: keyField.type,
        skip: false,
      };

      if (
        keyField.name &&
        this.keyFields.some((i) => i.name === keyField.name)
      ) {
        data.key = true;
      }

      if (keyField.id && !data.key) {
        data.id = keyField.id;
      }

      if (!keyField.id && !data.key) {
        data = { label: keyField.name, type: keyField.type, skip: false };
      }

      Vue.set(this.fields, keyField.value, data);
    },

    prepareString(s) {
      return (s || '')
        .replace(/[^a-zA-Z0-9]+/g, '')
        .trim()
        .toLowerCase();
    },

    findFieldKey(k) {
      const key = this.prepareString(k);
      const field = this.defaultBucketFields.find((i) => {
        return i.name === key || i.label === key;
      });
      return field ? field.field : '';
    },

    setFields() {
      Object.keys(this.importedFields).forEach((key) => {
        const fieldName = this.findFieldKey(this.importedFields[key]);
        let existingField = this.getSingleField(fieldName);

        if (fieldName === 'phone') {
          if (!this.importedFields.some((i) => i === 'mobile')) {
            existingField = this.getSingleField('mobile');
          }
        }

        if (existingField?.id) {
          this.assignField({
            id: existingField.id,
            name: existingField.name,
            bucketId: existingField.bucketId,
            type: existingField.type,
            value: key,
          });

          let keyField = this.keyFields.find(
            (i) => i.name === existingField.name
          );

          if (keyField) {
            keyField.value = key;
          }
        } else {
          this.assignField({
            name: this.importedFields[key],
            type: 'string',
            value: key,
          });
        }
      });
    },

    handleStatusUpdate(statusData) {
      const stats = statusData.stats || {};
      const total = (stats.imported || 0) + (stats.pending || 0);
      const processed =
        (stats.imported || 0) + (stats.finished || 0) + (stats.failed || 0);

      this.$emit('import-progress', {
        total,
        processed,
        isImporting: (stats.pending || 0) > 0,
        isComplete: (stats.pending || 0) === 0 && total > 0,
      });
    },
  },
  mounted() {
    this.checkFields();
    this.setFields();
  },
};
</script>

<style lang="less">
.import-contacts-content {
  .content-main__centered {
    max-width: 640px;
    margin: 12px auto;
  }

  .import-placeholder {
    .styled-list li {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 6px;
      text-align: left;
    }
  }
}
</style>
